// userService.js
import { db, storage } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getBatches } from "./batchService";

const studentsCollection = collection(db, "users");

export const getStudents = async () => {
  const q = query(studentsCollection, where("role", "==", "student"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Get both students and tutors
export const getEmployees = async () => {
  const q = query(studentsCollection, where("role", "in", ["student", "tutor"]));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getTutors = async () => {
  const q = query(studentsCollection, where("role", "==", "tutor"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getadmins = async () => {
  const q = query(studentsCollection, where("role", "==", "admin"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const addStudent = async (student) => {
  const payload = {
    ...student,
    createdAt: new Date(),
    isActive: true,
    role: "student",
  };
  const docRef = await addDoc(studentsCollection, payload);
  return { id: docRef.id, ...payload };
};

export const addTutor = async (tutor) => {
  const q = query(studentsCollection, where("email", "==", tutor.email));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    return { message: "Email already registered." };
  }

  const payload = {
    ...tutor,
    createdAt: Date.now(),
    role: "tutor",
  };

  const docRef = await addDoc(studentsCollection, payload);
  return { id: docRef.id, ...payload };
};

export const addAdmin = async (admin) => {
  const payload = {
    ...admin,
    createdAt: Date.now(),
    role: "admin",
  };
  const docRef = await addDoc(studentsCollection, payload);
  return { id: docRef.id, ...payload };
};

export const updateUser = async (id, user) => {
  const payload = {
    ...user,
    updatedAt: Date.now(),
  };
  const userDoc = doc(studentsCollection, id);
  await updateDoc(userDoc, payload);
  return { id, ...payload };
};

export const deleteUser = async (id) => {
  try {
    console.log(`Starting deletion process for user ID: ${id}`);

    // Get user details before deletion for logging
    const userDoc = doc(studentsCollection, id);
    const userSnapshot = await getDoc(userDoc);
    const userData = userSnapshot.exists() ? userSnapshot.data() : null;

    // Step 1: Delete attendance records
    try {
      const attendanceCollection = collection(db, "attendance");
      const attendanceQuery = query(attendanceCollection, where("userId", "==", id));
      const attendanceSnapshot = await getDocs(attendanceQuery);

      console.log(`Found ${attendanceSnapshot.docs.length} attendance records to delete`);

      for (const doc of attendanceSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting attendance records:", error);
    }

    // Step 2: Delete leave applications
    try {
      const leavesCollection = collection(db, "leaves");
      const leavesQuery = query(leavesCollection, where("userId", "==", id));
      const leavesSnapshot = await getDocs(leavesQuery);

      console.log(`Found ${leavesSnapshot.docs.length} leave records to delete`);

      for (const doc of leavesSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting leave records:", error);
    }

    // Step 3: Delete assigned tasks
    try {
      const tasksAssignedCollection = collection(db, "tasks_assigned");
      const tasksQuery = query(tasksAssignedCollection, where("userId", "==", id));
      const tasksSnapshot = await getDocs(tasksQuery);

      console.log(`Found ${tasksSnapshot.docs.length} task assignments to delete`);

      for (const doc of tasksSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting task assignments:", error);
    }

    // Step 4: Delete salary configurations
    try {
      const salariesCollection = collection(db, "salaries");
      const salariesQuery = query(salariesCollection, where("userId", "==", id));
      const salariesSnapshot = await getDocs(salariesQuery);

      console.log(`Found ${salariesSnapshot.docs.length} salary configurations to delete`);

      for (const doc of salariesSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting salary configurations:", error);
    }

    // Step 5: Delete salary records
    try {
      const salaryRecordsCollection = collection(db, "salary_records");
      const salaryRecordsQuery = query(salaryRecordsCollection, where("userId", "==", id));
      const salaryRecordsSnapshot = await getDocs(salaryRecordsQuery);

      console.log(`Found ${salaryRecordsSnapshot.docs.length} salary records to delete`);

      for (const doc of salaryRecordsSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting salary records:", error);
    }

    // Step 6: Delete classes (if user is a tutor)
    try {
      const classesCollection = collection(db, "classes");
      const classesQuery = query(classesCollection, where("userId", "==", id));
      const classesSnapshot = await getDocs(classesQuery);

      console.log(`Found ${classesSnapshot.docs.length} classes to delete`);

      for (const doc of classesSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting classes:", error);
    }

    // Step 7: Delete student activities
    try {
      const activitiesCollection = collection(db, "student_activities");
      const activitiesQuery = query(activitiesCollection, where("studentId", "==", id));
      const activitiesSnapshot = await getDocs(activitiesQuery);

      console.log(`Found ${activitiesSnapshot.docs.length} activity records to delete`);

      for (const doc of activitiesSnapshot.docs) {
        await deleteDoc(doc.ref);
      }
    } catch (error) {
      console.warn("Error deleting activity records:", error);
    }

    // Step 8: Finally delete the user document
    await deleteDoc(userDoc);

    console.log(`Successfully deleted user ${userData?.name || id} and all associated records`);

    return {
      id,
      message: `User ${userData?.name || id} and all associated records deleted successfully`,
      deletedRecords: {
        user: 1,
        attendance: "checked",
        leaves: "checked",
        tasks: "checked",
        salaries: "checked",
        salaryRecords: "checked",
        classes: "checked",
        activities: "checked"
      }
    };
  } catch (error) {
    console.error("Error in comprehensive user deletion:", error);
    throw new Error(`Failed to delete user and associated records: ${error.message}`);
  }
};

export const loginUser = async (email, password) => {
  console.log(email, password);
  const userQuery = query(studentsCollection, where("email", "==", email));
  const userSnapshot = await getDocs(userQuery);

  if (userSnapshot.empty) {
    return { message: "You are not registered. Please sign up." };
  }

  const user = userSnapshot.docs[0].data();

  if (user.password !== password) {
    return { message: "Invalid password" };
  }

  if (!user.isActive) {
    return {
      message: "Your account is inactive. Please contact administrator.",
    };
  }

  return { id: userSnapshot.docs[0].id, ...user };
};

export const updateUserPassword = async (studentId, password) => {
  if (!studentId || !password) throw new Error("Invalid input");

  const studentRef = doc(db, "users", studentId);

  await updateDoc(studentRef, {
    password, // replace with hash in real app
    passwordUpdatedAt: new Date(),
  });
};

export const registerStudent = async (studentData) => {
  const emailQuery = query(
    studentsCollection,
    where("email", "==", studentData.email)
  );
  const snapshot = await getDocs(emailQuery);

  const mobileQuery = query(
    studentsCollection,
    where("phone", "==", studentData.phone)
  );
  const mobileSnapshot = await getDocs(mobileQuery);

  if (!snapshot.empty) {
    return { message: "Email already registered." };
  }

  if (!mobileSnapshot.empty) {
    return { message: "Mobile number already registered." };
  }

  try {
    let resumeUrl = "";

    // Upload resume to Firebase Storage (handle quota errors gracefully)
    let resumeUploadFailed = false;
    let resumeUploadMessage = "";
    if (studentData.resume) {
      try {
        const resumeFileName = `${studentData.name
          ?.toLowerCase()
          .split(" ")
          .join("_")}_${studentData.resume.name}`;
        const storageRef = ref(storage, `resumes/${resumeFileName}`);
        await uploadBytes(storageRef, studentData.resume);
        resumeUrl = await getDownloadURL(storageRef);
      } catch (err) {
        // If storage quota exceeded or other storage errors, mark upload as failed
        console.warn("Resume upload failed:", err);
        resumeUploadFailed = true;
        // include both code and message for better diagnosis
        resumeUploadMessage = `${err?.code || "storage/error"}: ${err?.message || "Resume upload failed."}`;
        // continue without blocking registration
        resumeUrl = "";
      }
    }

    // Get all batches and find "Batch 2"
    const batches = await getBatches();
    const batch2 = batches.find((batch) => batch.name === "Batch 2");
    const defaultBatches = batch2 ? [{ id: batch2.id, name: batch2.name }] : [];

    const payload = {
      name: studentData.name,
      email: studentData.email,
      phone: studentData.phone,
      resumeUrl,
      isActive: false,
      role: "student",
      batches: defaultBatches,
      studentLevel: studentData.studentLevel,
      createdAt: new Date(),
      enrollmentDate: new Date().toISOString().slice(0, 10),
    };

    // Save student data in Firestore
    const docRef = await addDoc(studentsCollection, payload);

    return { id: docRef.id, ...studentData, resumeUrl, resumeUploadFailed, resumeUploadMessage };
  } catch (error) {
    console.error("Error adding student: ", error);
    throw error;
  }
};

export const updateUserStatus = async (studentId, isActive) => {
  if (!studentId) throw new Error("Invalid input");

  const studentRef = doc(db, "users", studentId);

  await updateDoc(studentRef, {
    isActive,
    updatedAt: Date.now(),
  });
};
